import {
  useCompany,
  useContractTypesByCompany,
  useAccessory,
  useProducts,
  useLinkProductToAccessory,
  useUnlinkProductFromAccessory,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {errorAtom, getCompanyCurrencyAtom, getUserSelectionAtom, pageLinksAtom, successMessageAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  activeCell,
  formatCurrency,
  formatPercentage,
  FormFieldItem,
  generateDropdownOptions,
  generateReadableErrorMessages,
  isCompanyUser,
  unlinkButtonCell,
  renderClickableCell,
  useViewNavigate,
  getUserData,
} from 'common'
import {TabButtonProps} from 'components/componsed-components'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {UIKit} from '@components'

export interface ProductsTableData {
  name: string
  price: string
  active: boolean
}

export enum AccessoryTab {
  GENERAL,
  LINKED_ITEMS,
}

export function useAccessoryViewModel() {
  const setError = useSetAtom(errorAtom)
  const setSuccessMessage = useSetAtom(successMessageAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const [isLinkModalShown, setIsLinkModalShown] = useState<boolean>(false)
  const [productToLink, setProductToLink] = useState<string | undefined>(undefined)
  const [productFormFieldError, setProductFormFieldError] = useState<string>('')
  const [activeTab, setActiveTab] = useState<AccessoryTab>(AccessoryTab.GENERAL)
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data} = useAccessory(id)

  const {data: productsResponse} = useProducts()
  const {data: contractTypesData} = useContractTypesByCompany(userSelection.companyId)
  const {data: companyData} = useCompany(data?.companyId || '')

  const {mutate: linkProductToAccessoryMutation} = useLinkProductToAccessory()
  const {mutate: unlinkProductFromAccessoryMutation} = useUnlinkProductFromAccessory()

  const productOptions = useMemo(() => {
    if (!productsResponse) return []
    else
      return generateDropdownOptions(
        productsResponse?.filter(item => !data?.products?.map(product => product.id).includes(item.id)),
        'name',
        'id',
      )
  }, [productsResponse, data])

  const contractTypePrices = useMemo(() => {
    if (!data) return []
    else
      return data?.contractTypePrices?.map(item => ({
        contractType: contractTypesData?.find(contractType => contractType.id === item.contractTypeId)?.name || '-',
        price: item.price,
        residualValue: item.residualValue,
        servicePrice: item.servicePrice,
        installationCompensation: item.installationCompensation,
        override: item.override,
      }))
  }, [data, contractTypesData])

  const productsRows = useMemo(() => {
    if (!data) return []
    else
      return data
        ? data?.products.map(item => ({
            name: item.name,
            price: formatCurrency(item.price, currency),
            active: item.active,
            id: item.id,
            unlinkId: item.id,
          }))
        : []
  }, [data])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey['Products and accessories']), link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: t(TranslationKey['Accessory Search']), link: NavigationPathKey.ACCESSORY_SEARCH},
        {title: t(TranslationKey['Accessory view'])},
      ]),
    [],
  )

  /**
   * Only Administrator Users should be available to edit
   */
  const isEditButtonVisible = !isCompanyUser()

  /**
   * Dynamically created text for the modal
   */
  const modalText: string = useMemo(
    () => `${t(TranslationKey['Choose a product to link to'])} ${data?.name}`,
    [data?.name],
  )

  /**
   *  Tab Buttons configuration
   */
  const tabButtons: TabButtonProps<AccessoryTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: AccessoryTab.GENERAL,
      onClick: () => setActiveTab(AccessoryTab.GENERAL),
    },
    {
      label: t(TranslationKey['Linked products']),
      key: AccessoryTab.LINKED_ITEMS,
      onClick: () => setActiveTab(AccessoryTab.LINKED_ITEMS),
    },
  ]

  /**
   * Product table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1.5,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.PRODUCT_VIEW}/${params.row.id}`)),
    },
    {field: 'price', headerName: t(TranslationKey.Price), flex: 0.5, headerAlign: 'right', align: 'right'},
    activeCell(t),
    ...(isEditButtonVisible ? [unlinkButtonCell(t, id => onUnlink(id))] : []),
  ]

  /**
   * Accessory form fields configuration - general section
   */
  const accessoryGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Product number']),
      text: data?.productNo,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Product name']),
      text: data?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companyData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: data?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * Accessory price form fields configuration
   * @param index Index of the price form field section
   */
  const accessoryPriceFormFields = (index?: number): FormFieldItem[] => [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Price),
      text: formatCurrency(index !== undefined ? contractTypePrices[index].price : data?.price || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Residual value']),
      text: formatPercentage(index !== undefined ? contractTypePrices[index].residualValue : data?.residualValue || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Installation compensation']),
      text: formatCurrency(
        index !== undefined ? contractTypePrices[index].installationCompensation : data?.installationCompensation || 0,
        currency,
      ),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Service price']),
      text: formatCurrency(
        index !== undefined ? contractTypePrices[index].servicePrice : data?.servicePrice || 0,
        currency,
      ),
    },
  ]

  /**
   * Accessory monthly price override form field configuration
   * @param index Index of the price form field section
   */
  const accessoryOverridePriceFormFields = (index?: number): FormFieldItem[] =>
    index !== undefined
      ? contractTypePrices[index].override?.map(item => ({
          fieldType: FormFieldType.LABEL,
          label: `${item.contractLength} ${t(TranslationKey.Months)}`,
          text: formatCurrency(item.price, currency),
        }))
      : []

  const linkProductFormField: FormFieldItem = {
    fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
    items: productOptions,
    value: productToLink,
    onChange: (item: UIKit.DropdownItem | null) => {
      setProductToLink(String(item?.value))
      setProductFormFieldError('')
    },
    error: !!productFormFieldError,
    placeholder: t(TranslationKey['Choose a product to link to']),
    listboxHeight: 100,
  }

  /**
   * A function that handles unlinking the Accessory from a Product
   * @param productId Product ID
   * @returns void
   */
  const onUnlink = (productId: string) => {
    if (id)
      unlinkProductFromAccessoryMutation(
        {accessoryId: id, productId},
        {onSuccess: () => setSuccessMessage(t(TranslationKey['Successfully unlinked a product']))},
      )
  }

  /**
   * A function that handles linking the Accessory to a Product
   * @returns void
   */
  const onLink = () => {
    if (!productToLink) {
      setProductFormFieldError('productToLink')
      setError({
        message: generateReadableErrorMessages(t, ['productToLink']),
        name: '',
      })
      return
    }

    setIsLinkModalShown(false)
    setProductToLink(undefined)
    if (id)
      linkProductToAccessoryMutation(
        {
          accessoryId: id,
          productId: productToLink,
        },
        {onSuccess: () => setSuccessMessage(t(TranslationKey['Successfully linked a product']))},
      )
  }

  /**
   * A function to handle Back button click
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.ACCESSORY_SEARCH)
  }

  /**
   * A function to handle Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.ACCESSORY_CREATION}/${id}`)
  }

  /**
   * A function to handle closing of the modal
   */
  const onCloseLinkModal = () => {
    setIsLinkModalShown(false)
    setProductToLink(undefined)
    setProductFormFieldError('')
  }

  /**
   * Only administrator users should be able to create new Products
   */
  const showCreateButton = !isCompanyUser()

  return {
    accessoryGeneralFormFields,
    productsRows,
    isLinkModalShown,
    columns,
    activeTab,
    tabButtons,
    isEditButtonVisible,
    modalText,
    linkProductFormField,
    t,
    contractTypePrices,
    showCreateButton,
    accessoryPriceFormFields,
    navigate,
    accessoryOverridePriceFormFields,
    onLink,
    setIsLinkModalShown,
    onPressBack,
    onPressEdit,
    onCloseLinkModal,
  }
}
